import { getConfig, klinikenApi } from "@/api";
import { Sambi } from "@/store/modules/authModule";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    convertToSubsetName,
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getNllKodverkOptions } from "@/store/modules/recept/helpers";
import { openNLLErrorDialog } from "@/tabs/Receptforskrivning/helpers";
import { BasePatientInfo, LakemedelDisplay, NLLPatientInfo, NllSamtycke } from "@/tabs/Receptforskrivning/types";
import { PersonIdTyp } from "@/utils/constants";
import cloneDeep from "lodash/cloneDeep";

/**
 * Används för att automatiskt generera funktioner och för typning av state
 */
const collectionNames = {
    administreringsmetoder: "administreringsmetoder",
    administreringsvagar: "administreringsvagar",
    administreringsstallen: "administreringsstallen",
    preciseringar: "preciseringar",
    medicintekniskaProdukter: "medicintekniskaProdukter",
    behandlingsorsaker: "behandlingsorsaker",
    tidsenheter: "tidsenheter",
};

export type NllKodverk = {
    /**
     * Samma värde som code. Används som unikt Id för objektet och sätts vid hämtning och sättning av data i store
     */
    id: NonNullable<NllKodverk["code"]>;
    code: string | null;
    patient_display?: string | null;
    display: string | null;
};

export type SessionData = {
    contextId: string;
    accessToken?: string;
    accessConsent: string | null;
    localAccess?: boolean;
};

type State = BaseState & {
    [key in keyof typeof collectionNames]: Map<NllKodverk["code"], NllKodverk>;
} & {
    patientinfo: NLLPatientInfo | BasePatientInfo | null;
    sessionData: SessionData | null;
};

type FetchArgs = {
    limit: number;
    page: number;
    filterParams: FilterParams & { nplPackId?: string; nplId?: string };
    apiUrl: string;
    collectionName: keyof typeof collectionNames;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        administreringsmetoder: new Map(),
        administreringsvagar: new Map(),
        administreringsstallen: new Map(),
        preciseringar: new Map(),
        medicintekniskaProdukter: new Map(),
        behandlingsorsaker: new Map(),
        tidsenheter: new Map(),
        patientinfo: null,
        sessionData: null,
        ...cloneDeep(baseState),
    };
};

/**
 * Generisk funktion som används för hämtning av nll-kodverk, specifikt inom actions fetch*
 * Skicka med nplId och nplPackId för att göra en hämtnng via drugoptions som returnerar flera olika samlingar baserat
 * på inskickade ID:n (läkemedel). Annars hämtars hela samlingar oberoende av läkemedel
 */
const fetch = async (
    { commit, state, getters }: BaseActionsProps<State>,
    { limit = 0, page = 1, filterParams, collectionName, apiUrl }: FetchArgs
) => {
    try {
        const isDrugSearch = filterParams?.nplPackId && filterParams?.nplId;

        const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Omit<NllKodverk, "id">>({
            filterParams,
            state,
            limit,
            page,
            collectionName,
            config: {
                headers: getters["headers"],
            },
            apiUrl: isDrugSearch ? "sil9/drugoptions/" : apiUrl,
        });

        if (!results) {
            return {
                // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
                subsetName, // Totala mängden träffar från sökningen
                total, // Nummer för nästa page
                nextPage,
            };
        }

        const collections = isDrugSearch
            ? Object.fromEntries(
                  Object.entries(results)
                      .filter(([key, value]) => value)
                      .map(([key, value]) => [
                          key,
                          {
                              ...value,
                              id: value.code,
                          },
                      ])
              )
            : {
                  [collectionName]: results.map((result) => ({
                      ...result,
                      id: result.code,
                  })),
              };

        commit("addKodverk", {
            offset,
            collections,
            subsetName,
        });

        return {
            // Namnet på subsettet (en ihopslagning av sökparams eller det du skickade in till actionen
            subsetName, // Totala mängden träffar från sökningen
            total, // Nummer för nästa page
            nextPage,
        };
    } catch (error: any) {
        console.error(error);

        openNLLErrorDialog({ error, description: "Kunde inte hämta kodverk." });

        throw error;
    }
};
export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Funktioner utifrån collectionNames (t.ex administreringsmetoder, administreringsstallen). Returnerar
         * hela samlingar, likt andra moduler i store
         */
        ...Object.fromEntries(
            (Object.keys(collectionNames) as Array<keyof typeof collectionNames>).map(
                (collectionName: keyof typeof collectionNames) => {
                    return [collectionName, (state: State) => [...state[collectionName].values()]];
                }
            )
        ),
        /**
         * Funktioner utifrån collectionNames (t.ex administreringsmetoderSubset, administreringsstallenSubset).
         * Return är ett subset, likt andra moduler i store. Namnsättningen på subsets är:
         * *namn på samling*-*subset name*. T.ex: administreringsmetoder-default. Görs för att stödja flera samlingar
         * inom samma modul och att de alla kan ha sina egna subsets
         */
        ...Object.fromEntries(
            (Object.keys(collectionNames) as Array<keyof typeof collectionNames>).map(
                (collectionName: keyof typeof collectionNames) => {
                    return [
                        `${collectionName}Subset`,
                        (state: State) => (props: SubsetGetterProps<State>) =>
                            getNllKodverkOptions({
                                data: subsetGetter<State>({
                                    ...props,
                                    subsetName: `${collectionName}-${props.subsetName}`,
                                    toArray: true,
                                    limit: 0,
                                    state,
                                    collectionName,
                                }) as NllKodverk[],
                            }),
                    ];
                }
            )
        ),
        /**
         * Funktioner utifrån collectionNames (t.ex administreringsmetoderById, administreringsstallenById). Return är
         * 1 träff, likt andra moduler i store
         */
        ...Object.fromEntries(
            (Object.keys(collectionNames) as Array<keyof typeof collectionNames>).map(
                (collectionName: keyof typeof collectionNames) => {
                    return [
                        `${collectionName}ById`,
                        (state: State) =>
                            ({ id, displayMode }: { id?: string; displayMode?: boolean }) => {
                                if (!id) {
                                    return null;
                                }

                                if (displayMode) {
                                    const found = state[collectionName].get(id);

                                    if (found) {
                                        return getNllKodverkOptions({ data: [found] })[0];
                                    }

                                    return null;
                                }

                                return state[collectionName].get(id);
                            },
                    ];
                }
            )
        ),
        patientinfo: (state: State) => {
            return state.patientinfo;
        },
        sessionData: (
            state: State,
            _: any,
            rootState: {
                auth: {
                    data: {
                        sambi?: Sambi;
                    };
                };
            }
        ) => {
            return {
                ...state.sessionData,
                accessToken: rootState.auth.data.sambi?.access_token?.ticket,
            };
        },
        headers: (
            state: State,
            _: any,
            rootState: {
                auth: {
                    data: {
                        sambi?: Sambi;
                    };
                };
            }
        ) => {
            const headers: {
                "x-kliniken-saml-assertion"?: string;
                "x-kliniken-context-id"?: string;
                "x-kliniken-access-consent"?: string;
            } = {};

            if (rootState.auth.data.sambi?.access_token?.ticket) {
                headers["x-kliniken-saml-assertion"] = rootState.auth.data.sambi.access_token.ticket;
            }

            if (state.sessionData?.contextId) {
                headers["x-kliniken-context-id"] = state.sessionData.contextId;
            }

            if (state.sessionData?.accessConsent) {
                headers["x-kliniken-access-consent"] = state.sessionData.accessConsent;
            }

            return headers;
        },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName: "nll" });
            },
        }),
        addKodverk: (
            state: State,
            {
                resetSubset,
                offset = 0,
                subsetName = "default",
                collections = {} as { [key in keyof typeof collectionNames]: Array<NllKodverk> },
            }: {
                offset: number;
                collections: { [key in keyof typeof collectionNames]: Array<NllKodverk> };
                resetSubset?: boolean;
                subsetName?: keyof Subsets;
            }
        ) => {
            Object.entries(collections).forEach(([collectionName, items]) => {
                subsetSetter<State, NllKodverk>({
                    offset,
                    state,
                    items,
                    resetSubset,
                    subsetName: `${collectionName}-${subsetName}`,
                    collectionName: collectionName as keyof typeof collectionNames,
                });
            });
        },
        setPatientinfo: (state: State, data: NLLPatientInfo | BasePatientInfo) => {
            state.patientinfo = data;
        },
        setContextId: (state: State, data: { contextId: string }) => {
            state.sessionData = state.sessionData
                ? {
                      ...cloneDeep(state.sessionData),
                      contextId: data.contextId,
                      accessConsent: null,
                  }
                : {
                      contextId: data.contextId,
                      accessConsent: null,
                  };
        },
        setAccessConsent: (state: State, data: { accessConsent: string | null; skipPatientinfo?: boolean }) => {
            if (!state.sessionData) {
                return;
            }

            state.sessionData = {
                ...cloneDeep(state.sessionData),
                accessConsent: data.accessConsent,
            };

            if (!data.skipPatientinfo && state.patientinfo) {
                state.patientinfo = {
                    ...cloneDeep(state.patientinfo as NLLPatientInfo),
                    samtycke: data.accessConsent,
                };
            }
        },
        setLocalAccess: (state: State, data: { localAccess: boolean }) => {
            if (!state.sessionData) {
                return;
            }

            state.sessionData = {
                ...cloneDeep(state.sessionData),
                localAccess: data.localAccess,
            };
        },

        resetSessionData: (state: State) => {
            state.sessionData = null;
        },
        setConsentRequest: (state: State, data: { consentRequest: string }) => {
            if (state.patientinfo) {
                state.patientinfo = {
                    ...cloneDeep(state.patientinfo as NLLPatientInfo),
                    samtyckesforfragan_finns: data.consentRequest,
                };
            }
        },
        resetPatientinfo: (state: State) => {
            state.patientinfo = null;
        },
        setPatientinfoLoading: (state: State, { loading }: { loading: boolean }) => {
            if (state.patientinfo) {
                const patientinfo = cloneDeep(state.patientinfo);

                state.patientinfo = {
                    ...patientinfo,
                    loading,
                };
            }
        },
    },
    actions: {
        ...baseActions({
            moduleName: "nll",
            onRemoveSubscriber: (args: BaseActionsProps<State>) => {
                const { state, commit } = args;

                if (!state.subscribers) {
                    commit("resetPatientinfo");
                }
            },
        }),
        fetchAdministreringsmetoder: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/administreringsmetoder/",
                collectionName: "administreringsmetoder",
            }),
        fetchAdministreringsvagar: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/administreringsvagar/",
                collectionName: "administreringsvagar",
            }),
        fetchAdministreringsstallen: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/administreringsstallen/",
                collectionName: "administreringsstallen",
            }),
        fetchPreciseringar: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/preciseringar/",
                collectionName: "preciseringar",
            }),
        fetchMedicintekniskaProdukter: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/medicintekniskaprodukter/",
                collectionName: "medicintekniskaProdukter",
            }),
        fetchBehandlingsorsaker: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/behandlingsorsaker/",
                collectionName: "behandlingsorsaker",
            }),
        fetchTidsenheter: (vuexArgs: BaseActionsProps<State>, args: FetchArgs) =>
            fetch(vuexArgs, {
                ...args,
                apiUrl: "nll/kodverk/tidsenheter/",
                collectionName: "tidsenheter",
            }),
        /**
         * Hämtar alla värden som ska sättas som options i olika autocompletes baserat på inskickad nplId och
         * lakemedelId (nplPackId/id).
         * Om inte nplId skickas in kommer en hämtning av kodverk göras oberoende av läkemedel och lagras i default
         */
        fetchKodverkByLakemedel: async (
            { commit, state, getters }: BaseActionsProps<State>,
            {
                nplId,
                lakemedelId,
            }: {
                nplId?: LakemedelDisplay["nplId"];
                lakemedelId?: LakemedelDisplay["id"];
            } = {}
        ) => {
            try {
                const { data } = await klinikenApi.get<{
                    administreringsmetoder: NllKodverk[] | null;
                    administreringsvagar: NllKodverk[] | null;
                    behandlingsorsaker: NllKodverk[] | null;
                    administreringsstallen: NllKodverk[] | null;
                    preciseringar: NllKodverk[] | null;
                    medicintekniskaprodukter: NllKodverk[] | null;
                }>(
                    nplId && lakemedelId ? `nll/lakemedelsval/${nplId}/` : "nll/lakemedelsval/",
                    getConfig({
                        headers: getters["headers"],
                    })
                );

                commit("addKodverk", {
                    resetSubset: true,
                    subsetName:
                        nplId && lakemedelId
                            ? convertToSubsetName({
                                  params: {
                                      nplId,
                                      nplPackId: lakemedelId,
                                  },
                              })
                            : undefined,
                    collections: {
                        administreringsmetoder:
                            data.administreringsmetoder?.map((metod) => ({ ...metod, id: metod.code })) ?? [],
                        administreringsvagar: data.administreringsvagar?.map((vag) => ({ ...vag, id: vag.code })) ?? [],
                        behandlingsorsaker:
                            data.behandlingsorsaker?.map((orsak) => ({ ...orsak, id: orsak.code })) ?? [],
                        administreringsstallen:
                            data.administreringsstallen?.map((stalle) => ({ ...stalle, id: stalle.code })) ?? [],
                        medicintekniskaProdukter:
                            data.medicintekniskaprodukter?.map((produkt) => ({ ...produkt, id: produkt.code })) ?? [],
                        preciseringar:
                            data.preciseringar?.map((precisering) => ({ ...precisering, id: precisering.code })) ?? [],
                    },
                });
            } catch (error: any) {
                openNLLErrorDialog({ error, description: "Det gick inte att hämta kodverk för läkemedel." });

                throw error;
            }
        },
        fetchPatientinfo: async (
            { commit, dispatch, rootGetters, state, getters }: BaseActionsProps<State>,
            { accessConsent }: { accessConsent?: string } = {}
        ) => {
            try {
                if (state.patientinfo) {
                    commit("setPatientinfoLoading", { loading: true });
                }

                const headers = getters["headers"] as { [key: string]: string };

                /*
                    Tillåter skickning av custom access consent. Används när nytt samtycke läggs till lokalt och
                    patientinfo ska uppdateras
                 */
                if (accessConsent) {
                    headers["x-kliniken-access-consent"] = accessConsent;
                }

                const response = await klinikenApi.get(
                    `/nll/patientinfo/${rootGetters["patientData/currentId"]}/`,
                    getConfig({
                        headers,
                    })
                );

                const patientinfo = cloneDeep(response.data);
                patientinfo.patient.personIdType = PersonIdTyp.PERSONNUMMER;

                /*
                     Om samtycke är satt lokalt kommer samtycke ignoreras från BE. Det är bara när det inte är satt som
                     det kommer sparas från BE, då BE skickar det sparade samtycket och sparar bara DOS-samtycke,
                     Tillfälligt Samtycke eller Registrerat åtkomstamtycke.
                 */
                if (
                    (patientinfo.samtycke === null && (accessConsent || state.sessionData?.accessConsent)) ||
                    (patientinfo.samtycke &&
                        (!!(accessConsent && patientinfo.samtycke !== accessConsent) ||
                            !!(
                                state.sessionData?.accessConsent &&
                                patientinfo.samtycke !== state.sessionData?.accessConsent
                            )))
                ) {
                    accessConsent = accessConsent ?? (state.sessionData?.accessConsent as string);
                    patientinfo.samtycke = accessConsent ?? (state.sessionData?.accessConsent as string);
                } else {
                    accessConsent = patientinfo.samtycke;
                }

                commit("setLocalAccess", { localAccess: false });

                commit("setPatientinfo", {
                    ...patientinfo,
                    loading: false,
                });

                commit("setAccessConsent", { accessConsent });
            } catch (error: any) {
                // 404 ges specifikt om patient inte hittades hos NLL
                if (error.response.status === 404) {
                    // Visar intern patientdata
                    await dispatch("loadInternalPatientinfo");

                    commit("setAccessConsent", {
                        accessConsent: NllSamtycke.UtanKrav,
                        skipPatientinfo: true,
                    });

                    return;
                }

                console.error(error);

                openNLLErrorDialog({ error, description: "Kunde inte hämta patient från NLL." });
            }
        },
        loadInternalPatientinfo: ({ commit, rootGetters }: BaseActionsProps<State>) => {
            const patient = rootGetters["patientData/patient"] as {
                id: string;
                personIdType: string;
                personId: string;
                fornamn: string;
                efternamn: string;
                dateOfBirth: Date;
                careCoverage: boolean;
            };

            commit("setPatientinfo", {
                patient: {
                    kliniken_id: patient.id,
                    personId: patient.personId,
                    personIdType: patient.personIdType,
                    fornamn: patient.fornamn,
                    efternamn: patient.efternamn,
                    dateOfBirth: patient.dateOfBirth,
                },
                formansberattigad: patient.careCoverage,
                loading: false,
            });
            commit("setLocalAccess", { localAccess: true });
        },
    },
};
